/* eslint-disable no-nested-ternary */
import React, { useState } from 'react';
import useSWR from 'swr';
import DropDown from './DropDown';
import Modal from './Modal';
import SelectBox from './SelectBox';
import { SelectBoxItemType } from '../../types/SelectBoxPropsType';
import Utils from '../../utils/Utils';
import { useAppContext } from '../../utils/AppContext';

import { ReactComponent as WarningIcon } from '../../img/icons/warning.svg';
import { postRequest } from '../../utils/fetch';
import { AddToFotaCampaignProps, AddToFotaCampaignResponse } from '../../types/AddToFotaCampaignProps';
import { DtFotaCampaign } from '../../types/FotaCampaigns';

const listWidth = 205;
const defaultMaxItems = 4;

function AddToDtFotaCampaign(props: AddToFotaCampaignProps): JSX.Element {
  const validCampaignStates = ['ACTIVE', 'CREATED'];

  const {
    selectedItems,
    closeDropdown,
  } = props;

  const [selectedCampaign, setSelectedCampaign] = useState({
    title: 'Select',
    key: '0',
  });

  const { data: dtFotaCampaignsResp } = useSWR<DtFotaCampaign[]>(
    [`/fota/campaign?filter=${validCampaignStates.join(',')}&campaignType=DT`, 'GetDtFotaCampaigns'],
  );

  const { addNotification } = useAppContext();
  const [openModal, setOpenModal] = useState(false);

  const createAddNodesResponseMessage = (
    totalNodesAdded: number,
    duplicates: string[],
    alreadyInProgress: string[],
  ): string => {
    const duplicateNodesMessage = duplicates.length > 0
      ? `There are ${duplicates.length} node(s) already added to this DTFOTA campaign. Please visit the DTFOTA campaign page to view details.`
      : '';
    const alreadyInProgressNodesMessage = alreadyInProgress.length > 0
      ? `The following node(s) have ongoing firmware upgrade processes in other DTFOTA campaigns: ${alreadyInProgress.join(', ')}`
      : '';
    return `Your "Add to DTFOTA campaign" operation is completed. ${totalNodesAdded} node(s) were successfully added to the campaign. ${duplicateNodesMessage} ${alreadyInProgressNodesMessage}`;
  };

  return (
    <div className="multi-node-modals">
      {!openModal && (
      <DropDown
        title="Add to DTFOTA campaign"
        secondaryButtonType="secondary"
        secondaryButtonLabel="Submit"
        secondaryButtonSize="small"
        secondaryButtonAction={() => {
          if (selectedItems.size === 0) {
            addNotification({ type: 'warning', message: 'No nodes were selected for the operation.' });
          } else if (selectedCampaign.key === '0') {
            addNotification({ type: 'warning', message: 'No DTFOTA campaign is selected for the operation.' });
          } else {
            setOpenModal(true);
          }
        }}
        primaryButtonLabel="Cancel"
        primaryButtonType="primary"
        primaryButtonSize="small"
        primaryButtonAction={() => closeDropdown()}
        className="toolbar-action-dropdown"
      >
        <SelectBox
          label="Select DTFOTA campaign"
          type="dark"
          list={
            (dtFotaCampaignsResp
              && Utils.arrayToSelectbox(dtFotaCampaignsResp, 'campaignId', 'name'))
            || []
          }
          onClick={(item: SelectBoxItemType) => {
            setSelectedCampaign(item);
          }}
          title={selectedCampaign.title}
          selectedItemKey={selectedCampaign.key}
          listWidth={listWidth}
          maxItems={defaultMaxItems}
        />
      </DropDown>
      )}
      {openModal && (
        <Modal
          title="Confirm action"
          width="500"
          setModalOpen={() => {
            setOpenModal(false);
            closeDropdown();
          }}
          primaryButtonLabel="Submit"
          primaryButtonAction={async () => {
            try {
              const nodeLimit = 1000;
              const nodeKeys = Array.from(selectedItems.keys());
              const totalBatches = Math.ceil(nodeKeys.length / nodeLimit);
              const responses: AddToFotaCampaignResponse[] = [];
              let hasError = false;
              const processBatchesSequentially = async () => {
                for (let i = 0; i < totalBatches; i += 1) {
                  const postBody = { nodes: nodeKeys.slice(i * nodeLimit, i * nodeLimit + nodeLimit) };
                  // eslint-disable-next-line no-await-in-loop
                  const result = await postRequest<{ nodes: string[] }, AddToFotaCampaignResponse>(
                    `/fota/campaign/${selectedCampaign.key}/addNodes`,
                    postBody,
                  );
                  if (!result.error && !hasError) {
                    responses.push(result.data as AddToFotaCampaignResponse);
                  } else if (!result.error) {
                    // do nothing
                  } else if (!hasError) {
                    addNotification({ type: 'error', message: `Your "Add to DTFOTA campaign" operation has failed: ${result.error}` });
                    hasError = true;
                  }
                }
              };
              await processBatchesSequentially();
              if (!hasError && responses.length > 0) {
                const totalNodesAdded = responses.reduce((sum, resp) => sum + (resp.operationSummary.nodesAdded || 0), 0);
                const totalNodesFailed = responses.reduce((sum, resp) => sum + (resp.operationSummary.nodesFailed || 0), 0);
                const duplicates = responses.flatMap((resp) => (resp.operationDetails.failedNodes.duplicates || []));
                const alreadyInProgress = responses.flatMap((resp) => (resp.operationDetails.failedNodes.alreadyInProgress || []));
                const message = createAddNodesResponseMessage(totalNodesAdded, duplicates, alreadyInProgress);
                const type = totalNodesFailed > 0 ? 'warning' : 'info';
                addNotification({ type, message });
                setOpenModal(false);
                closeDropdown();
              }
            } catch (e) {
              addNotification({ type: 'error', message: 'Your "Add to DTFOTA campaign" operation has failed.' });
            }
          }}
          secondaryButtonLabel="Cancel"
          borderTopColor="yellow"
          className="add-node-to-fota-campaign"
        >
          <div className="add-node-to-fota-campaign__confirm">
            <WarningIcon />
            <div>
              {selectedItems.size > 0
            && (
              <div>
                <span>
                  {selectedItems.size}
                  &nbsp;
                </span>
                node(s) will be added to
                <span>
                  &nbsp;
                  { selectedCampaign.title }
                </span>
                .
              </div>
            )}
              <div className="add-node-to-fota-campaign__warning">
                This DTFOTA campaign currently has
                {' '}
                <span>{dtFotaCampaignsResp?.find((c) => c.campaignId === selectedCampaign.key)?.numberOfNodes || 'N/A'}</span>
                {' '}
                nodes and will update all to firmware version
                {' '}
                <span>{dtFotaCampaignsResp?.find((c) => c.campaignId === selectedCampaign.key)?.targetVersion || 'N/A'}</span>
                . Please submit or cancel.
              </div>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
}

export default AddToDtFotaCampaign;
